import { ArrowButtonLeftBold, ArrowButtonRightBold } from '@ha/icons';
import * as React from 'react';
import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { Imgix } from 'ha/modules/Image/Imgix';
import { SlideRendererParams } from 'ha/components/Slider';
import { VirtualizeSlider } from 'ha/components/Slider/VirtualizeSlider';
import { ListingPhoto } from '@ha/api/v2/types/Listing';
import { IconButton, units } from '@hbf/dsl';

const useStyles = makeStyles()(theme => ({
  media: {
    position: 'relative',
    width: '100%',
    paddingTop: '66.66%' /* 3:2 Aspect Ratio */,
    [theme.breakpoints.up('md')]: {
      paddingTop: '50%' /* 2:1 Aspect Ratio */,
    },
  },
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    cursor: 'pointer',
  },
  imageContainerBorder: {
    borderRadius: 0,
    [theme.breakpoints.up('md')]: {
      borderRadius: units.rem(0.25),
    },
  },
  arrowButton: {
    color: theme.palette.white.main,
    filter: `drop-shadow(0px 1px 3px ${theme.palette.grey[700]})`,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    '&:disabled': {
      color: alpha(theme.palette.white.main, 0.5),
    },
  },
  leftButton: {
    left: theme.spacing(1.5),
  },
  rightButton: {
    right: theme.spacing(1.5),
  },
  favoriteButton: {
    marginLeft: 'auto',
  },
  imageCover: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  fullHeight: {
    height: '100%',
  },
  badge: {
    background: theme.palette.white.main,
    color: theme.palette.orange.dark,
    borderRadius: units.rem(0.875),
    fontSize: units.rem(0.875),
    padding: units.yx(0.18, 0.55),
  },
  cancellationBadge: {
    color: theme.palette.grey['700'],
  },
  moreMediaOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: alpha(theme.palette.grey[700], 0.65),
  },
}));

export type Props = {
  photos: ListingPhoto[];
  onClick: (index: number) => void;
};

const ImageSlider = ({ photos, onClick }: Props) => {
  const [index, setIndex] = React.useState(0);
  const { classes, cx } = useStyles();
  const { T } = useIntl();

  const slideCount = photos.length;
  const isFirstSlide = index === 0;
  const isLastSlide = index === slideCount - 1;

  const handlePrevIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isFirstSlide) {
        setIndex(index - 1);
      }
    },
    [index, isFirstSlide],
  );

  const handleNextIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLastSlide) {
        setIndex(index + 1);
      }
    },
    [index, isLastSlide],
  );

  return slideCount > 1 ? (
    <div data-test-locator="Listing/ImageSlider" className={classes.media}>
      <div className={cx(classes.absolute, classes.fullHeight)}>
        <VirtualizeSlider
          overscanSlideAfter={isLastSlide ? 0 : 1}
          overscanSlideBefore={isFirstSlide ? 0 : 1}
          slideCount={slideCount}
          style={{ height: '100%' }}
          containerStyle={{ height: '100%' }}
          index={index}
          onChangeIndex={setIndex}
          slideRenderer={({
            index: currentIndex,
            key,
          }: SlideRendererParams) => {
            const photo = photos?.[currentIndex];

            if (!photo) {
              return null;
            }

            return (
              <div
                onClick={() => onClick(currentIndex)}
                className={cx(
                  classes.imageContainer,
                  classes.imageContainerBorder,
                )}
                key={key}
              >
                <Imgix
                  className={classes.imageCover}
                  htmlAttributes={{ role: 'presentation' }}
                  imgixParams={{ cs: 'srgb' }}
                  sizes="100vw"
                  src={photo.url}
                />
              </div>
            );
          }}
        />
      </div>
      <IconButton
        disabled={isFirstSlide}
        disableRipple={isFirstSlide}
        edge="start"
        aria-label={T('Previous image')}
        className={cx(classes.arrowButton, classes.leftButton)}
        onClick={handlePrevIndex}
        size="large"
      >
        <ArrowButtonLeftBold />
      </IconButton>
      <IconButton
        disabled={isLastSlide}
        disableRipple={isLastSlide}
        edge="end"
        aria-label={T('Next image')}
        className={cx(classes.arrowButton, classes.rightButton)}
        onClick={handleNextIndex}
        size="large"
      >
        <ArrowButtonRightBold />
      </IconButton>
    </div>
  ) : (
    <Imgix
      className={classes.imageCover}
      htmlAttributes={{
        'data-test-locator': 'Listing/CardPhoto',
        role: 'presentation',
      }}
      imgixParams={{ cs: 'srgb' }}
      src={photos[0].url}
      sizes="100vw"
    />
  );
};

export { ImageSlider };
